import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Database,
  ExternalLink,
  FileSpreadsheet,
  FilterX,
  Inbox,
  LoaderCircle,
  MessageSquareText,
  Search,
  Settings2,
  Sheet,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent,
} from 'react';
import { useAuth } from '../auth/useAuth';
import {
  applyProcessingView,
  fetchProcessingDataset,
  type ProcessingAvailability,
  type ProcessingColumn,
  type ProcessingDataset,
  type ProcessingFilters,
  type ProcessingSort,
  type ProcessingSource,
} from '../lib/processing-data';
import {
  isProcessingDatasetCacheFresh,
  readCachedProcessingDataset,
  writeCachedProcessingDataset,
} from '../lib/processing-dataset-cache';
import { requireFirebaseFunctions } from '../lib/firebase';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
const ROW_HEIGHT = 46;
const HEADER_HEIGHT = 99;
const OVERSCAN = 8;
const COLUMN_VISIBILITY_COOKIE_PREFIX = 'reportcrm-hidden-columns-';
const COLUMN_VISIBILITY_MAX_AGE = 60 * 60 * 24 * 365;

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function readHiddenColumnKeys(source: ProcessingSource): string[] {
  if (typeof document === 'undefined') return [];
  const cookieName = `${COLUMN_VISIBILITY_COOKIE_PREFIX}${source}=`;
  const cookie = document.cookie.split('; ').find((item) => item.startsWith(cookieName));
  if (!cookie) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(cookie.slice(cookieName.length)));
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeHiddenColumnKeys(source: ProcessingSource, keys: string[]): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COLUMN_VISIBILITY_COOKIE_PREFIX}${source}=${encodeURIComponent(JSON.stringify(keys))}; Max-Age=${COLUMN_VISIBILITY_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

function datasetCacheKey(source: ProcessingSource, year: number, month: number | null): string {
  return `${source}:${year}:${month ?? 'latest'}`;
}

const demoAvailability: ProcessingAvailability = {
  masterYears: [2026, 2025, 2024],
  mail: [{
    year: 2026,
    months: [9],
    latestReferenceDate: '2026-09-22',
    latestFileName: 'Report 22.09.2026.xlsx',
  }],
  responses: [{ year: 2026, months: [9] }],
};

function demoDataset(source: ProcessingSource, year: number, month: number | null): ProcessingDataset {
  if (source === 'responses') {
    const columns: ProcessingColumn[] = [
      { key: 'informazioni cronologiche', label: 'Informazioni cronologiche', sourceIndex: 0 },
      { key: 'indirizzo email', label: 'Indirizzo email', sourceIndex: 1 },
      { key: 'quanti casi hai inviato al reparto', label: 'Quanti casi hai inviato al reparto?', sourceIndex: 2 },
      { key: 'data di riferimento', label: 'Data di riferimento', sourceIndex: 3 },
    ];
    const rows = [
      ['23/09/2026, 19:36', 'francesca.tomassini@justbit.it', '3', '23/09/2026'],
      ['23/09/2026, 19:41', 'jessica.flore@justbit.it', '12', '23/09/2026'],
    ];
    return {
      source,
      year,
      month,
      file: {
        id: 'demo-responses',
        name: 'Risposte - [Report CRM - OF] - Casi inviati al reparto',
        path: 'Google Sheets / Risposte - [Report CRM - OF] - Casi inviati al reparto',
        modifiedTime: '2026-09-23T18:14:20.584Z',
        webViewLink: null,
      },
      referenceDate: null,
      worksheetName: 'Risposte del modulo 1',
      columns,
      rows,
      rowCount: rows.length,
    };
  }

  const columns: ProcessingColumn[] = [
    { key: 'numero caso', label: 'Numero caso', sourceIndex: 0 },
    { key: 'stato', label: 'Stato', sourceIndex: 1 },
    { key: 'origine caso', label: 'Origine caso', sourceIndex: 2 },
    { key: 'data apertura', label: 'Data apertura', sourceIndex: 3 },
    { key: 'provincia2', label: 'Provincia2', sourceIndex: 4 },
    { key: 'argomento', label: 'Argomento', sourceIndex: 5 },
    { key: 'social reason', label: 'Social Reason', sourceIndex: 6 },
  ];
  const rows = [
    ['28296762', 'Chiuso', 'Web', '22/09/2026', 'Salerno', 'Informazioni Generali', 'Copertura'],
    ['28296705', 'In Lavorazione', 'Web', '22/09/2026', 'Napoli', 'Problemi', 'Copertura'],
    ['28296688', 'Chiuso', 'Facebook', '21/09/2026', '', '', ''],
    ['28296680', 'Chiuso', 'Web', '21/09/2026', 'Modena', 'Problemi', 'Delivery'],
  ];
  const fileName = source === 'master' ? `${year}.xlsx` : 'Report 22.09.2026.xlsx';
  return {
    source,
    year,
    month,
    file: {
      id: 'demo',
      name: fileName,
      path: source === 'master'
        ? `Export / Export annuale / ${fileName}`
        : `Mail / ${year} / 09. Settembre / ${fileName}`,
      modifiedTime: '2026-09-22T10:23:42.201Z',
      webViewLink: null,
    },
    referenceDate: source === 'mail' ? '2026-09-22' : null,
    worksheetName: 'Report Segnalazioni Utenti Soci',
    columns,
    rows,
    rowCount: rows.length,
  };
}

function formatModifiedTime(value: string | null): string {
  if (!value) return 'Data non disponibile';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data non disponibile';
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function columnWidth(column: ProcessingColumn): number {
  const normalized = column.key;
  if (/note|indirizzo|social reason|social subreason/.test(normalized)) return 260;
  if (/data|telefono|email/.test(normalized)) return 178;
  if (/numero caso|stato|origine/.test(normalized)) return 164;
  return Math.min(230, Math.max(150, column.label.length * 8 + 58));
}

function ProcessingDataTable(props: {
  columns: ProcessingColumn[];
  rows: string[][];
  filters: ProcessingFilters;
  sort: ProcessingSort;
  onFilter: (key: string, value: string) => void;
  onSort: (key: string) => void;
}) {
  const headerScrollerRef = useRef<HTMLDivElement>(null);
  const bodyScrollerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(620);
  const widths = useMemo(() => props.columns.map(columnWidth), [props.columns]);
  const tableWidth = useMemo(() => widths.reduce((total, width) => total + width, 0), [widths]);
  const template = useMemo(() => widths.map((width) => `${width}px`).join(' '), [widths]);
  const visibleStart = Math.max(0, Math.floor(Math.max(0, scrollTop - HEADER_HEIGHT) / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
  const visibleRows = props.rows.slice(visibleStart, visibleStart + visibleCount);

  useEffect(() => {
    const element = bodyScrollerRef.current;
    if (!element) return;
    let animationFrame = 0;
    const updateVisibleWindow = () => {
      animationFrame = 0;
      setScrollTop(Math.max(0, -element.getBoundingClientRect().top));
      setViewportHeight(window.innerHeight);
    };
    const scheduleUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(updateVisibleWindow);
    };
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(element);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    updateVisibleWindow();
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    const element = bodyScrollerRef.current;
    if (element) setScrollTop(Math.max(0, -element.getBoundingClientRect().top));
  }, [props.rows]);

  const syncHeaderScroll = (event: UIEvent<HTMLDivElement>) => {
    const header = headerScrollerRef.current;
    if (header && header.scrollLeft !== event.currentTarget.scrollLeft) {
      header.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const syncBodyScroll = (event: UIEvent<HTMLDivElement>) => {
    const body = bodyScrollerRef.current;
    if (body && body.scrollLeft !== event.currentTarget.scrollLeft) {
      body.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  return (
    <div className="processing-table" role="table" aria-rowcount={props.rows.length} aria-colcount={props.columns.length}>
      <div className="processing-table-header-sticky">
        <div className="processing-table-horizontal-scroll processing-table-header-scroll" ref={headerScrollerRef} onScroll={syncBodyScroll}>
          <div className="processing-grid-header" role="rowgroup" style={{ width: tableWidth, minWidth: '100%' }}>
            <div className="processing-grid-row processing-grid-labels" role="row" style={{ gridTemplateColumns: template }}>
              {props.columns.map((column) => {
                const activeSort = props.sort?.key === column.key ? props.sort.direction : null;
                const SortIcon = activeSort === 'asc' ? ArrowUp : activeSort === 'desc' ? ArrowDown : ArrowUpDown;
                return (
                  <button
                    type="button"
                    role="columnheader"
                    aria-sort={activeSort === 'asc' ? 'ascending' : activeSort === 'desc' ? 'descending' : 'none'}
                    key={column.key}
                    onClick={() => props.onSort(column.key)}
                  >
                    <span>{column.label}</span><SortIcon size={13} />
                  </button>
                );
              })}
            </div>
            <div className="processing-grid-row processing-grid-filters" role="row" style={{ gridTemplateColumns: template }}>
              {props.columns.map((column) => (
                <label key={column.key} role="columnheader">
                  <Search size={12} />
                  <input
                    type="search"
                    value={props.filters[column.key] ?? ''}
                    placeholder="Filtra…"
                    aria-label={`Filtra ${column.label}`}
                    onChange={(event) => props.onFilter(column.key, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="processing-table-horizontal-scroll processing-table-body-scroll" ref={bodyScrollerRef} onScroll={syncHeaderScroll}>
        <div className="processing-table-inner" style={{ width: tableWidth, minWidth: '100%' }}>
          <div className="processing-grid-body" role="rowgroup" style={{ height: props.rows.length * ROW_HEIGHT }}>
            {visibleRows.map((row, visibleIndex) => {
              const rowIndex = visibleStart + visibleIndex;
              return (
                <div
                  className="processing-grid-row processing-grid-data-row"
                  role="row"
                  aria-rowindex={rowIndex + 2}
                  key={rowIndex}
                  style={{
                    gridTemplateColumns: template,
                    transform: `translateY(${rowIndex * ROW_HEIGHT}px)`,
                  }}
                >
                  {props.columns.map((column) => (
                    <span className={column.sourceIndex === 0 ? 'primary' : undefined} role="cell" key={column.key} title={row[column.sourceIndex] ?? ''}>{row[column.sourceIndex] || '—'}</span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ElaborationsPage() {
  const { user } = useAuth();
  const datasetCacheRef = useRef(new Map<string, ProcessingDataset>());
  const datasetCachedAtRef = useRef(new Map<string, number>());
  const datasetRequestsRef = useRef(new Map<string, Promise<ProcessingDataset>>());
  const expectedMailVersionRef = useRef(new Map<string, string>());
  const foregroundRequestRef = useRef(0);
  const columnSettingsRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<ProcessingSource>('master');
  const [availability, setAvailability] = useState<ProcessingAvailability | null>(null);
  const [year, setYear] = useState<number | ''>(() => new Date().getFullYear());
  const [month, setMonth] = useState<number | ''>('');
  const [dataset, setDataset] = useState<ProcessingDataset | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [filters, setFilters] = useState<ProcessingFilters>({});
  const deferredFilters = useDeferredValue(filters);
  const [sort, setSort] = useState<ProcessingSort>(null);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [hiddenColumnsBySource, setHiddenColumnsBySource] = useState<Partial<Record<ProcessingSource, string[]>>>(() => ({
    master: readHiddenColumnKeys('master'),
    mail: readHiddenColumnKeys('mail'),
    responses: readHiddenColumnKeys('responses'),
  }));

  const fetchRemoteDataset = useCallback((
    source: ProcessingSource,
    selectedYear: number,
    selectedMonth: number | null,
    options: {
      onProgress?: (loaded: number, total: number) => void;
    } = {},
  ): Promise<ProcessingDataset> => {
    const key = datasetCacheKey(source, selectedYear, selectedMonth);
    const inFlight = datasetRequestsRef.current.get(key);
    if (inFlight) return inFlight;

    const request = user?.demo
      ? Promise.resolve(demoDataset(source, selectedYear, selectedMonth))
      : fetchProcessingDataset({
          source,
          year: selectedYear,
          month: selectedMonth,
          onProgress: options.onProgress,
        });
    const trackedRequest = request
      .then((result) => {
        datasetCacheRef.current.set(key, result);
        datasetCachedAtRef.current.set(key, Date.now());
        if (!user?.demo && user?.uid) {
          void writeCachedProcessingDataset({ userId: user.uid, dataset: result }).catch(() => undefined);
        }
        return result;
      })
      .finally(() => {
        datasetRequestsRef.current.delete(key);
      });
    datasetRequestsRef.current.set(key, trackedRequest);
    return trackedRequest;
  }, [user]);

  const getDataset = useCallback(async (
    source: ProcessingSource,
    selectedYear: number,
    selectedMonth: number | null,
    options: {
      force?: boolean;
      onProgress?: (loaded: number, total: number) => void;
      onBackgroundRefresh?: (dataset: ProcessingDataset) => void;
    } = {},
  ): Promise<ProcessingDataset> => {
    const key = datasetCacheKey(source, selectedYear, selectedMonth);
    const isCurrentVersion = (candidate: ProcessingDataset) => {
      if (source !== 'mail' || selectedMonth !== null) return true;
      const expected = expectedMailVersionRef.current.get(key);
      return !expected || expected === `${candidate.referenceDate ?? ''}:${candidate.file.name}`;
    };

    if (!options.force) {
      let cached = datasetCacheRef.current.get(key);
      let cachedAt = datasetCachedAtRef.current.get(key) ?? 0;

      if (!cached && !user?.demo && user?.uid) {
        try {
          const persistent = await readCachedProcessingDataset({
            userId: user.uid,
            source,
            year: selectedYear,
            month: selectedMonth,
          });
          if (persistent) {
            cached = persistent.dataset;
            cachedAt = persistent.cachedAt;
            datasetCacheRef.current.set(key, cached);
            datasetCachedAtRef.current.set(key, cachedAt);
          }
        } catch {
          // La cache browser è un'ottimizzazione: in caso di errore si usa Drive.
        }
      }

      if (cached && isCurrentVersion(cached)) {
        if (!isProcessingDatasetCacheFresh(cachedAt, Date.now(), source)) {
          void fetchRemoteDataset(source, selectedYear, selectedMonth)
            .then((freshDataset) => options.onBackgroundRefresh?.(freshDataset))
            .catch(() => undefined);
        }
        return cached;
      }
      if (cached) {
        datasetCacheRef.current.delete(key);
        datasetCachedAtRef.current.delete(key);
      }
    }

    return fetchRemoteDataset(source, selectedYear, selectedMonth, {
      onProgress: options.onProgress,
    });
  }, [fetchRemoteDataset, user]);

  const showDataset = useCallback(async (
    source: ProcessingSource,
    selectedYear: number,
    selectedMonth: number | null,
    force = false,
  ) => {
    const requestId = ++foregroundRequestRef.current;
    setColumnsMenuOpen(false);
    setError(null);
    setFilters({});
    setSort(null);

    setStatus('loading');
    setDataset(null);
    setProgress({ loaded: 0, total: 0 });

    try {
      const result = await getDataset(source, selectedYear, selectedMonth, {
        force,
        onProgress: (loaded, total) => {
          if (requestId === foregroundRequestRef.current) setProgress({ loaded, total });
        },
        onBackgroundRefresh: (freshDataset) => {
          if (requestId !== foregroundRequestRef.current) return;
          setDataset(freshDataset);
          setProgress({ loaded: freshDataset.rows.length, total: freshDataset.rowCount });
        },
      });
      if (requestId !== foregroundRequestRef.current) return;
      setDataset(result);
      setProgress({ loaded: result.rows.length, total: result.rowCount });
      setStatus('ready');
    } catch (loadError) {
      if (requestId !== foregroundRequestRef.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Non è stato possibile caricare la tabella.');
      setStatus('error');
    }
  }, [getDataset]);

  useEffect(() => {
    let active = true;
    const currentYear = new Date().getFullYear();
    const loadDefaultMaster = async () => {
      await Promise.resolve();
      if (active) await showDataset('master', currentYear, null);
    };
    void loadDefaultMaster();

    const load = async () => {
      try {
        const result = user?.demo
          ? demoAvailability
          : (await httpsCallable<void, ProcessingAvailability>(
              requireFirebaseFunctions(),
              'getProcessingAvailability',
            )()).data;
        if (!active) return;
        result.mail.forEach((item) => {
          expectedMailVersionRef.current.set(
            datasetCacheKey('mail', item.year, null),
            `${item.latestReferenceDate}:${item.latestFileName}`,
          );
        });
        const masterYear = result.masterYears.includes(currentYear)
          ? currentYear
          : result.masterYears[0] ?? result.mail[0]?.year ?? currentYear;
        const mailYear = result.mail.some(({ year: item }) => item === currentYear)
          ? currentYear
          : result.mail[0]?.year;
        setAvailability(result);
        if (masterYear !== currentYear) {
          setYear(masterYear);
          void showDataset('master', masterYear, null);
        }
        if (mailYear !== undefined) {
          void getDataset('mail', mailYear, null).catch(() => undefined);
        }
      } catch {
        if (active) {
          setAvailability({ masterYears: [], mail: [], responses: [] });
        }
      }
    };
    void load();
    return () => { active = false; };
  }, [getDataset, showDataset, user?.demo]);

  useEffect(() => () => { foregroundRequestRef.current += 1; }, []);

  useEffect(() => {
    if (!columnsMenuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!columnSettingsRef.current?.contains(event.target as Node)) setColumnsMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setColumnsMenuOpen(false);
    };
    window.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [columnsMenuOpen]);

  const availableYears = useMemo(() => {
    const tabYears = activeTab === 'master'
      ? availability?.masterYears ?? []
      : activeTab === 'mail'
        ? availability?.mail.map(({ year: item }) => item) ?? []
        : availability?.responses?.map(({ year: item }) => item) ?? [];
    const fallbacks = availability?.masterYears ?? [];
    return [...new Set([...tabYears, ...fallbacks])].sort((left, right) => right - left);
  }, [activeTab, availability]);

  const availableMonths = useMemo(
    () => activeTab === 'mail'
      ? availability?.mail.find(({ year: item }) => item === year)?.months ?? []
      : activeTab === 'responses'
        ? availability?.responses?.find(({ year: item }) => item === year)?.months ?? []
        : [],
    [activeTab, availability, year],
  );

  const visibleRows = useMemo(
    () => dataset ? applyProcessingView(dataset.rows, dataset.columns, deferredFilters, sort) : [],
    [dataset, deferredFilters, sort],
  );
  const hiddenColumnKeys = useMemo(() => {
    if (!dataset) return [];
    const availableKeys = new Set(dataset.columns.map(({ key }) => key));
    const validKeys = (hiddenColumnsBySource[dataset.source] ?? []).filter((key) => availableKeys.has(key));
    return validKeys.length < dataset.columns.length ? validKeys : [];
  }, [dataset, hiddenColumnsBySource]);
  const visibleColumns = useMemo(() => {
    const hiddenKeys = new Set(hiddenColumnKeys);
    return dataset?.columns.filter(({ key }) => !hiddenKeys.has(key)) ?? [];
  }, [dataset, hiddenColumnKeys]);
  const hasViewChanges = sort !== null || Object.values(filters).some((value) => value.trim() !== '');

  const changeTab = (source: ProcessingSource) => {
    setActiveTab(source);
    setMonth('');
    const nextYears = source === 'master'
      ? availability?.masterYears ?? []
      : source === 'mail'
        ? availability?.mail.map(({ year: item }) => item) ?? []
        : availability?.responses?.map(({ year: item }) => item) ?? [];
    const currentYear = new Date().getFullYear();
    const nextYear = nextYears.includes(currentYear)
      ? currentYear
      : nextYears[0] ?? availability?.masterYears[0] ?? currentYear;
    setYear(nextYear);
    void showDataset(source, nextYear, null);
  };

  const loadDataset = async () => {
    if (year === '') return;
    await showDataset(activeTab, year, month === '' ? null : month, true);
  };

  const clearDatasetForPeriodChange = () => {
    foregroundRequestRef.current += 1;
    setDataset(null);
    setStatus('idle');
    setError(null);
    setFilters({});
    setSort(null);
    setProgress({ loaded: 0, total: 0 });
  };

  const updateFilter = (key: string, value: string) => setFilters((current) => ({ ...current, [key]: value }));
  const updateSort = (key: string) => setSort((current) => (
    current?.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }
  ));
  const resetView = () => {
    setFilters({});
    setSort(null);
  };
  const toggleColumnVisibility = (columnKey: string) => {
    if (!dataset) return;
    const nextHiddenKeys = new Set(hiddenColumnKeys);
    if (nextHiddenKeys.has(columnKey)) {
      nextHiddenKeys.delete(columnKey);
    } else {
      if (visibleColumns.length === 1) return;
      nextHiddenKeys.add(columnKey);
      setFilters((current) => {
        if (!(columnKey in current)) return current;
        const next = { ...current };
        delete next[columnKey];
        return next;
      });
      setSort((current) => current?.key === columnKey ? null : current);
    }
    const next = [...nextHiddenKeys];
    setHiddenColumnsBySource((current) => ({ ...current, [dataset.source]: next }));
    writeHiddenColumnKeys(dataset.source, next);
  };
  const showAllColumns = () => {
    if (!dataset) return;
    setHiddenColumnsBySource((current) => ({ ...current, [dataset.source]: [] }));
    writeHiddenColumnKeys(dataset.source, []);
  };

  const loadedPercent = progress.total > 0 ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : 0;

  return (
    <main className="dashboard-page elaborations-page">
      <header className="page-heading elaborations-heading">
        <div>
          <p className="app-eyebrow">Dati operativi</p>
          <h1>Source</h1>
          <p>Consulta i dati Master, l’ultimo report ricevuto via mail e le risposte inviate dal team.</p>
        </div>
        <span className="processing-live-chip"><Database size={14} /> Dati da Drive</span>
      </header>

      <div className="processing-tabs" role="tablist" aria-label="Origine dati">
        <button type="button" role="tab" aria-selected={activeTab === 'master'} className={activeTab === 'master' ? 'active' : undefined} onClick={() => changeTab('master')}>
          <Sheet size={18} /><span><strong>Master</strong><small>Export annuale</small></span>
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'mail'} className={activeTab === 'mail' ? 'active' : undefined} onClick={() => changeTab('mail')}>
          <Inbox size={18} /><span><strong>Mail</strong><small>Ultimo report ricevuto</small></span>
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'responses'} className={activeTab === 'responses' ? 'active' : undefined} onClick={() => changeTab('responses')}>
          <MessageSquareText size={18} /><span><strong>Risposte</strong><small>Casi inviati al reparto</small></span>
        </button>
      </div>

      <section className="processing-period-card" aria-label="Seleziona il periodo">
        <div className="processing-period-copy">
          <span><FileSpreadsheet size={21} /></span>
          <div>
            <p>Periodo dati</p>
            <h2>{activeTab === 'master'
              ? 'Scegli l’export Master'
              : activeTab === 'mail' ? 'Scegli il report Mail' : 'Scegli le risposte'}</h2>
            <small>{activeTab === 'master'
              ? 'Il mese è facoltativo: senza selezione viene mostrato l’intero file annuale.'
              : activeTab === 'mail'
                ? 'Senza mese viene aperto il file giornaliero più recente dell’anno.'
                : 'Il mese è facoltativo: senza selezione vengono mostrate tutte le risposte dell’anno.'}</small>
          </div>
        </div>
        <div className="processing-period-fields">
          <label>
            <span>Anno</span>
            <select value={year} disabled={status === 'loading'} onChange={(event) => { setYear(Number(event.target.value)); clearDatasetForPeriodChange(); }}>
              {availableYears.length === 0 && year !== '' && <option value={year}>{year}</option>}
              {availableYears.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label>
            <span>Mese</span>
            <select value={month} disabled={status === 'loading'} onChange={(event) => { setMonth(event.target.value ? Number(event.target.value) : ''); clearDatasetForPeriodChange(); }}>
              <option value="">{activeTab === 'mail' ? 'Ultimo disponibile' : 'Tutta l’annualità'}</option>
              {MONTHS.map((item, index) => (
                <option key={item} value={index + 1}>
                  {item}{activeTab !== 'master' && availableMonths.length > 0 && !availableMonths.includes(index + 1) ? ' — non disponibile' : ''}
                </option>
              ))}
            </select>
          </label>
          <button className="button button-primary" type="button" disabled={status === 'loading' || year === ''} onClick={() => void loadDataset()}>
            {status === 'loading' ? <LoaderCircle className="spin" size={17} /> : <Database size={17} />}
            {status === 'loading' ? 'Caricamento…' : 'Carica tabella'}
          </button>
        </div>
      </section>

      {status === 'idle' && (
        <section className="processing-empty-state">
          <span><Database size={25} /></span>
          <div><strong>La tabella apparirà qui</strong><p>Conferma anno e periodo per leggere i dati da Drive.</p></div>
        </section>
      )}

      {status === 'loading' && (
        <section className="processing-loading-state" aria-live="polite">
          <div className="processing-loading-icon"><LoaderCircle className="spin" size={25} /></div>
          <div>
            <strong>{progress.total > 0 ? 'Ricezione delle righe' : activeTab === 'responses' ? 'Lettura del foglio Risposte' : 'Lettura del file Excel'}</strong>
            <p>{progress.total > 0
              ? `${progress.loaded.toLocaleString('it-IT')} di ${progress.total.toLocaleString('it-IT')} righe`
              : 'Individuo il foglio dati e preparo le colonne.'}</p>
          </div>
          <div className="processing-progress"><span style={{ width: `${progress.total > 0 ? loadedPercent : 22}%` }} /></div>
        </section>
      )}

      {status === 'error' && (
        <section className="processing-empty-state error" role="alert">
          <span><FileSpreadsheet size={25} /></span>
          <div><strong>Tabella non disponibile</strong><p>{error}</p></div>
          <button className="button button-secondary" type="button" onClick={() => void loadDataset()}>Riprova</button>
        </section>
      )}

      {status === 'ready' && dataset && (
        <section className="processing-dataset-card">
          <header className="processing-file-header">
            <span className="processing-file-icon"><FileSpreadsheet size={18} /></span>
            <div className="processing-file-copy">
              <strong title={dataset.file.path}>{dataset.file.name}</strong>
              <span>
                {dataset.worksheetName}
                {' · '}
                {visibleRows.length === dataset.rowCount
                  ? `${dataset.rowCount.toLocaleString('it-IT')} righe`
                  : `${visibleRows.length.toLocaleString('it-IT')} di ${dataset.rowCount.toLocaleString('it-IT')} righe`}
                {' · Aggiornato '}{formatModifiedTime(dataset.file.modifiedTime)}
              </span>
            </div>
            <div className="processing-file-actions">
              <button className="processing-reset-view" type="button" disabled={!hasViewChanges} onClick={resetView}><FilterX size={14} /> Reimposta vista</button>
              {dataset.file.webViewLink && (
                <a className="button button-secondary" href={dataset.file.webViewLink} target="_blank" rel="noreferrer"><ExternalLink size={15} /> {activeTab === 'responses' ? 'Apri foglio' : 'Apri Excel'}</a>
              )}
              <div className="processing-column-picker" ref={columnSettingsRef}>
                <button
                  className="processing-column-trigger"
                  type="button"
                  aria-label="Scegli le colonne visibili"
                  aria-haspopup="dialog"
                  aria-expanded={columnsMenuOpen}
                  aria-controls="processing-column-popover"
                  title="Scegli le colonne visibili"
                  onClick={() => setColumnsMenuOpen((open) => !open)}
                >
                  <Settings2 size={16} />
                </button>
                {columnsMenuOpen && (
                  <div className="processing-column-popover" id="processing-column-popover" role="dialog" aria-label="Colonne visibili">
                    <header>
                      <div>
                        <strong>Colonne visibili</strong>
                        <span>{visibleColumns.length} di {dataset.columns.length}</span>
                      </div>
                      <button type="button" disabled={hiddenColumnKeys.length === 0} onClick={showAllColumns}>Mostra tutte</button>
                    </header>
                    <div className="processing-column-options">
                      {dataset.columns.map((column) => {
                        const checked = !hiddenColumnKeys.includes(column.key);
                        return (
                          <label key={column.key}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={checked && visibleColumns.length === 1}
                              onChange={() => toggleColumnVisibility(column.key)}
                            />
                            <span>{column.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          {visibleRows.length > 0 ? (
            <ProcessingDataTable
              columns={visibleColumns}
              rows={visibleRows}
              filters={filters}
              sort={sort}
              onFilter={updateFilter}
              onSort={updateSort}
            />
          ) : (
            <div className="processing-no-results"><Search size={22} /><strong>Nessuna riga corrisponde ai filtri</strong><p>Modifica i criteri nelle intestazioni o reimposta la vista.</p></div>
          )}
        </section>
      )}
    </main>
  );
}
